import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, Modal, ToggleRow } from '@/components/kit';
import apiClient from '@/services/apiClient';

/**
 * ALERT FAMILIES — `g_m_fams` (f2d). Eight real switches.
 *
 * WHAT INTERRUPTS A MANAGER, AND WHAT DOES NOT. A clinic that cannot turn one
 * of these off is a clinic whose manager learns to ignore all eight, so each is
 * a switch and each one is obeyed at the point the alert is PRODUCED — the
 * notification is never created, rather than created and hidden.
 *
 * THE SWITCH STOPS THE ALERT, NEVER THE LOG LINE. The board's own promise, and
 * it is the reason this gates notifications rather than events: a voided bill
 * is still voided, still audited and still on the money-edits report with the
 * family off. What stops is the interruption.
 *
 * SAVE IS THE COMMIT. The switches move locally and nothing is written until
 * Save, because a modal with a Save button that had already saved is a modal
 * whose Cancel lies. Closing without saving discards.
 */

interface Family {
  key: string;
  label: string;
  detail: string;
}

interface FamiliesPayload {
  families: Family[];
  state: Record<string, boolean>;
}

export function AlertFamiliesModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);

  const q = useQuery({
    queryKey: ['cm-alert-families'],
    enabled: open,
    staleTime: 60_000,
    queryFn: async (): Promise<FamiliesPayload> => {
      const res = await apiClient.get('/clinic-settings/alert-families');
      return (res.data?.data ?? res.data) as FamiliesPayload;
    },
  });

  /* The draft is seeded from the server every time the modal opens, so
     reopening after a discard shows what is actually saved rather than the
     switches somebody flicked and walked away from. */
  useEffect(() => {
    if (open && q.data) setDraft({ ...q.data.state });
    if (!open) setDraft(null);
  }, [open, q.data]);

  const save = useMutation({
    mutationFn: async (next: Record<string, boolean>) => {
      await apiClient.put('/clinic-settings/alert-families', next);
    },
    onSuccess: () => {
      toast.success('Saved. It applies to the next alert, not the ones already here.');
      void qc.invalidateQueries({ queryKey: ['cm-alert-families'] });
      onClose();
    },
    onError: () => toast.error('That was refused. Nothing has changed.'),
  });

  const families = q.data?.families ?? [];
  const state = draft ?? q.data?.state ?? {};
  const on = families.filter((f) => state[f.key] !== false).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Alert families"
      sub={
        families.length
          ? `${families.length} families · ${on} on · delivery in-app`
          : 'which kinds of thing are allowed to interrupt you'
      }
      width={640}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!draft || save.isPending}
            onClick={() => draft && save.mutate(draft)}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {q.isLoading ? (
        <p className="t-sec mut" style={{ paddingTop: 10 }}>
          Reading what interrupts you…
        </p>
      ) : q.isError ? (
        <p className="t-sec mut" style={{ paddingTop: 10 }}>
          The switches did not come back. Nothing has been changed, and every family is
          still on.
        </p>
      ) : (
        <div style={{ paddingTop: 10 }}>
          {families.map((f) => (
            <ToggleRow
              key={f.key}
              label={f.label}
              captionOn={f.detail}
              captionOff={`${f.detail} — off, so it never reaches you`}
              checked={state[f.key] !== false}
              onChange={(next) => setDraft({ ...state, [f.key]: next })}
            />
          ))}

          <Card kind="accent">
            <p className="t-sec brand">
              Every family is privacy-clean: doctors and branches are named, patients never
              are. Switching one off stops the alert, never the log line — the audit trail
              and the reports keep every event either way.
            </p>
          </Card>
        </div>
      )}
    </Modal>
  );
}
